/**
 * Hook for fetching and caching the authenticated user's effective permissions
 * from the API gateway's GET /api/v1/me/permissions endpoint.
 *
 * @module hooks/usePermissions
 * @see ARCH-005-MOD-005
 */
import { useState, useEffect, useCallback, useRef } from "react";
import client from "../api/client";
import { getActAsSessionRef, useImpersonationShellSignal } from "../api/impersonation";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PermissionsResponse {
  user_id: string;
  tenant_id: string;
  roles: string[];
  permissions: string[];
}

export interface UsePermissionsResult {
  /**
   * Set of effective permission strings for the current user. UNDEFINED only
   * when the fetch FAILED inside an impersonation binding: that failure is a
   * loud error, never an empty set (SPEC-FRONTEND-RULE-095(8)).
   */
  permissions: Set<string> | undefined;
  /**
   * True when the fetch failed WHILE an impersonation binding is live. The
   * SPA mounts render a loud error with retry on it and never fall through to
   * a permission gate - whose Access Denied screen carries a Logout button.
   */
  impersonatedFailure: boolean;
  /** Check whether the user holds a specific permission. */
  hasPermission: (key: string) => boolean;
  /** Whether the permission fetch is in progress. */
  loading: boolean;
  /** Error from the last permission fetch attempt, or null. */
  error: Error | null;
  /** Trigger a permission refetch (e.g. after workspace switch). */
  refetchPermissions: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches the user's effective permissions on mount and when the
 * access token or workspace changes.  Returns a stable `hasPermission(key)`
 * callback and a `loading` flag.
 *
 * When `VITE_AUTH_DISABLED` is set, all permission checks return `true`
 * (mirroring the existing `hasRole` behaviour in AuthContext).
 *
 * @param workspaceId - Current workspace ID. When it changes, permissions
 *   are refetched so workspace-scoped permissions stay current.
 */
export function usePermissions(
  authenticated: boolean,
  accessToken: string | null,
): UsePermissionsResult {
  const authDisabled = import.meta.env.VITE_AUTH_DISABLED === "true";
  // The impersonation re-check poll's refetch cue (roles_recheck_ok). Whether
  // a binding is live is NOT a dependency: the act-as identity reaches this
  // hook through the identity-keyed REMOUNT (RULE-095(2)), and the failure
  // branch below reads the synchronous act-as getter at the moment it runs.
  const { permissionsEpoch } = useImpersonationShellSignal();

  const [permissions, setPermissions] = useState<Set<string> | undefined>(
    authDisabled ? new Set(["*"]) : new Set(),
  );
  const [impersonatedFailure, setImpersonatedFailure] = useState(false);
  const [loading, setLoading] = useState(!authDisabled);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCounter, setFetchCounter] = useState(0);
  const hasFetchedRef = useRef(false);

  const refetchPermissions = useCallback(() => {
    setFetchCounter((c) => c + 1);
  }, []);

  useEffect(() => {
    // In auth-disabled dev mode, skip the fetch — all permissions granted.
    if (authDisabled) {
      setPermissions(new Set(["*"]));
      setLoading(false);
      return;
    }

    if (!authenticated || !accessToken) {
      setPermissions(new Set());
      setLoading(false);
      return;
    }

    let cancelled = false;
    if (!hasFetchedRef.current) {
      setLoading(true);
    }
    setError(null);
    setImpersonatedFailure(false);

    client
      .get<PermissionsResponse>("/me/permissions")
      .then((res) => {
        if (!cancelled) {
          setPermissions(new Set(res.data.permissions));
          setError(null);
        }
      })
      .catch((err) => {
        console.error("[usePermissions] Failed to fetch permissions:", err);
        if (!cancelled) {
          if (getActAsSessionRef() !== null) {
            // THE IMPERSONATED PATH ONLY (RULE-095(8)): a failure is a LOUD
            // error with retry and the set stays UNDEFINED - never an empty
            // set that would reach a gate's Access Denied screen and its
            // Logout button inside a binding.
            setPermissions(undefined);
            setImpersonatedFailure(true);
          } else {
            // CITED, NOT REPAIRED: rendering every failure as an empty set is
            // a pre-existing platform-wide defect owned outside
            // EPIC-IMPERSONATION (plan.md section 3.7 item 7). Unchanged.
            setPermissions(new Set());
          }
          setError(err instanceof Error ? err : new Error("Failed to load permissions"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          hasFetchedRef.current = true;
        }
      });

    return () => {
      cancelled = true;
    };
    // `permissionsEpoch` is the impersonation re-check poll's refetch cue.
  }, [authenticated, accessToken, authDisabled, fetchCounter, permissionsEpoch]);

  const hasPermission = useCallback(
    (key: string) => {
      if (!permissions) return false;
      // Dev mode wildcard.
      if (permissions.has("*")) return true;
      return permissions.has(key);
    },
    [permissions],
  );

  return { permissions, impersonatedFailure, hasPermission, loading, error, refetchPermissions };
}
